import { paths } from '@mc/paths'
import { NavigationEvents } from '@mc/track-event'
import i18nInstance from './i18n/i18nInstance'
import {
  trackNavClick,
  trackPlansClick,
  trackGiftCTAClick,
  productTypeTrackingMap,
} from './tracking'
import type { Product } from './types'

const I18N_NS = '@mc/global-navigation'

export interface GetProductsConfig {
  pathname: string
  query: Record<string, string>
  newForBusinessPageEnabled: boolean
  Callout?: React.ReactNode
  onShowCertificatesSection?: () => void
  productType: 'consumer' | 'enterprise' | 'certificates' | 'executive'
  onCallLinksEnabled?: boolean
  allAccessEnabled?: boolean
  // mccerts-2851_v1_core_nav_promo_signal — badge on the certificates header +
  // promo card at the bottom of the certificates accordion, in any menu.
  certificatesBadge?: React.ReactNode
  certificatesPromoCard?: React.ReactNode
  // flw-1235_v2_enterprise_nav_cta — resolved enterprise CTA copy. Callers are
  // components and own the bucket; this is a plain function, so the string is
  // injected. Falls back to `nav.cta.work` when omitted.
  enterpriseCtaCopy?: string
}

export interface GetProductsOverrides {
  trackBrowseSkillsCatalogCTAClick?: (args: {
    action: string
    level: string
    pathname: string
    query: Record<string, string>
    product?: string
  }) => () => void
  getAllAccessMobileProduct?: () => Product
}

export const getProducts = (
  {
    pathname,
    query,
    newForBusinessPageEnabled,
    Callout,
    onShowCertificatesSection,
    productType,
    onCallLinksEnabled = false,
    allAccessEnabled = false,
    certificatesBadge,
    certificatesPromoCard,
    enterpriseCtaCopy,
  }: GetProductsConfig,
  overrides?: GetProductsOverrides,
): Product[] => {
  const t = (key: string) => i18nInstance.t(key, { ns: I18N_NS })

  const consumerLinks = [
    {
      title: t('nav.cta.plans'),
      href: paths.checkout,
      onClick: () => {
        trackPlansClick(
          NavigationEvents.LEVELS.TERTIARY,
          productTypeTrackingMap[productType],
        )()
      },
      promo: true,
    },
    {
      title: t('nav.cta.gift'),
      href: paths.gift,
      onClick: () =>
        trackGiftCTAClick({
          level: NavigationEvents.LEVELS.TERTIARY,
          product: productTypeTrackingMap[productType],
        }),
    },
    {
      title: t('nav.cta.support'),
      href: paths.helpCenter,
      onClick: trackNavClick(
        t('nav.cta.support'),
        NavigationEvents.LEVELS.TERTIARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
    {
      title: t('nav.cta.login'),
      href: paths.logIn(),
      onClick: trackNavClick(
        t('nav.cta.login'),
        NavigationEvents.LEVELS.TERTIARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
  ]

  const certificateLinks = [
    {
      title: t('nav.cta.browseAllCertificates'),
      href: productType === 'certificates' ? undefined : paths.certificates,
      // mccerts-2851_v1_core_nav_promo_signal — on cert pages the pill sits on
      // this row; elsewhere it sits on the Certificates header (see badge below).
      badge: productType === 'certificates' ? certificatesBadge : undefined,
      onClick: () => {
        const trackingFunction = trackNavClick(
          t('nav.cta.browseAllCertificates'),
          NavigationEvents.LEVELS.TERTIARY,
          undefined,
          productTypeTrackingMap[productType],
        )

        trackingFunction()

        if (productType === 'certificates') {
          onShowCertificatesSection?.()
        }
      },
    },
    {
      title: t('nav.cta.login'),
      href: paths.logIn(paths.certificates),
      onClick: trackNavClick(
        t('nav.cta.login'),
        NavigationEvents.LEVELS.TERTIARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
  ]

  const enterpriseLinks = [
    ...(newForBusinessPageEnabled
      ? [
          {
            title: t('nav.cta.howItWorks'),
            href: paths.enterpriseHowItWorks,
            onClick: trackNavClick(
              t('nav.cta.howItWorks'),
              NavigationEvents.LEVELS.TERTIARY,
              undefined,
              productTypeTrackingMap[productType],
            ),
          },
        ]
      : []),
    {
      title: t('nav.cta.browseSkillsLibrary'),
      href: paths.enterpriseSkillsLibrary,
      onClick: overrides?.trackBrowseSkillsCatalogCTAClick
        ? overrides.trackBrowseSkillsCatalogCTAClick({
            action: t('nav.cta.browseSkillsLibrary'),
            level: NavigationEvents.LEVELS.TERTIARY,
            pathname,
            query,
            product: productTypeTrackingMap[productType],
          })
        : trackNavClick(
            t('nav.cta.browseSkillsLibrary'),
            NavigationEvents.LEVELS.TERTIARY,
            undefined,
            productTypeTrackingMap[productType],
          ),
    },
    {
      title: t('nav.cta.support'),
      href: paths.helpCenter,
      onClick: trackNavClick(
        t('nav.cta.support'),
        NavigationEvents.LEVELS.TERTIARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
    {
      title: t('nav.cta.login'),
      href: paths.logIn(),
      onClick: trackNavClick(
        t('nav.cta.login'),
        NavigationEvents.LEVELS.TERTIARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
  ]

  const onCallLinks = [
    ...(onCallLinksEnabled
      ? [
          {
            title: t('nav.cta.exploreOnCall'),
            href: paths.onCall,
            onClick: trackNavClick(
              t('nav.cta.exploreOnCall'),
              NavigationEvents.LEVELS.TERTIARY,
            ),
          },
          {
            title: t('nav.cta.login'),
            href: paths.onCallLogin,
            onClick: trackNavClick(
              t('nav.cta.login'),
              NavigationEvents.LEVELS.TERTIARY,
            ),
          },
        ]
      : []),
  ]

  return [
    {
      title: t('nav.cta.masterClass'),
      type: 'consumer',
      links: consumerLinks,
      callout: productType === 'consumer' ? Callout : undefined,
      separator: productType === 'consumer' && !!Callout,
      onClick: trackNavClick(
        t('nav.cta.masterClass'),
        NavigationEvents.LEVELS.SECONDARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
    {
      title: t('nav.cta.certificates'),
      type: 'certificates',
      links: certificateLinks,
      // mccerts-2851_v1_core_nav_promo_signal — the promo card, when present,
      // is the certificates callout in any menu (else the default cert callout).
      callout:
        certificatesPromoCard ??
        (productType === 'certificates' ? Callout : undefined),
      separator:
        !!certificatesPromoCard ||
        (productType === 'certificates' && !!Callout),
      // mccerts-2851_v1_core_nav_promo_signal — header pill in every menu except
      // the cert-page menu, where it sits on the Browse All Certificates row.
      badge: productType === 'certificates' ? undefined : certificatesBadge,
      onClick: trackNavClick(
        t('nav.cta.certificates'),
        NavigationEvents.LEVELS.SECONDARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
    ...(onCallLinksEnabled
      ? [
          {
            title: t('nav.cta.onCall'),
            type: 'onCall' as const,
            links: onCallLinks,
            onClick: trackNavClick(
              t('nav.cta.onCall'),
              NavigationEvents.LEVELS.SECONDARY,
              undefined,
              productTypeTrackingMap[productType],
            ),
          },
        ]
      : []),
    {
      title: t('nav.cta.executive'),
      type: 'executive' as const,
      links: [
        {
          title: t('nav.cta.exploreExecutive'),
          href: paths.executive,
          onClick: trackNavClick(
            t('nav.cta.exploreExecutive'),
            NavigationEvents.LEVELS.TERTIARY,
            undefined,
            productTypeTrackingMap[productType],
          ),
        },
      ],
      callout: productType === 'executive' ? Callout : undefined,
      separator: productType === 'executive' && !!Callout,
      onClick: trackNavClick(
        t('nav.cta.executive'),
        NavigationEvents.LEVELS.SECONDARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
    ...(allAccessEnabled && overrides?.getAllAccessMobileProduct
      ? [overrides.getAllAccessMobileProduct()]
      : []),
    {
      title: enterpriseCtaCopy ?? t('nav.cta.work'),
      type: 'enterprise',
      links: enterpriseLinks,
      callout: productType === 'enterprise' ? Callout : undefined,
      separator: productType === 'enterprise' && !!Callout,
      onClick: trackNavClick(
        enterpriseCtaCopy ?? t('nav.cta.work'),
        NavigationEvents.LEVELS.SECONDARY,
        undefined,
        productTypeTrackingMap[productType],
      ),
    },
  ]
}
